import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  GridLegacy as Grid,
  Card,
  CardContent,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  Fab,
  Chip,
  Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Schedule as ScheduleIcon,
  CalendarToday as CalendarIcon,
  EventAvailable as AvailableIcon,
  EventBusy as BookedIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { panelistAPI } from '../../services/api';
import AvailabilitySlotForm from './AvailabilitySlotForm';
import SlotsList from './SlotsList';

const AvailabilityManagement = () => {
  // ===== MAIN STATE =====
  const [activeTab, setActiveTab] = useState(0);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState({ open: false, message: '', severity: 'info' });

  // ✅ ADD: Request deduplication state
  const [lastRequestTime, setLastRequestTime] = useState(0);
  const loadingRef = useRef(false);
  const requestCache = useRef(new Map());

  // ===== PAGINATION STATE =====
  const [slotsPagination, setSlotsPagination] = useState({
    page: 1,
    per_page: 20,
    total: 0,
    pages: 1,
    has_next: false,
    has_prev: false,
    showing_start: 0,
    showing_end: 0
  });

  // ===== DIALOG STATE =====
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);

  // ===== FILTER STATE =====
  const [filterOptions, setFilterOptions] = useState({
    include_booked: true,
    start_date: '',
    end_date: ''
  });

  // ✅ OPTIMIZED: Load slots with deduplication and pagination
  const loadAvailabilitySlots = useCallback(async (page = 1, filters = {}, forceRefresh = false) => {
    const now = Date.now();
    const cacheKey = `slots-${page}-${JSON.stringify(filters)}`;

    // ✅ Prevent redundant calls
    if (!forceRefresh) {
      if (now - lastRequestTime < 1000 || loadingRef.current) {
        console.log('🚫 Skipping redundant slot loading call');
        return;
      }

      // Check cache
      const cached = requestCache.current.get(cacheKey);
      if (cached && now - cached.timestamp < 30000) { // 30 second cache
        console.log('🚀 Using cached slot data');
        setSlots(cached.data);
        setSlotsPagination(cached.pagination);
        setLoading(false);
        return;
      }
    }

    try {
      loadingRef.current = true;
      setLoading(true);
      setLastRequestTime(now);

      const params = {
        page,
        per_page: slotsPagination.per_page,
        ...filters
      };

      console.log('🗓️ Loading availability slots...', params);
      const response = await panelistAPI.getAvailabilitySlots(params);
      const slotsData = response.data.slots || [];
      const paginationData = response.data.pagination || {};

      // Cache the result
      requestCache.current.set(cacheKey, {
        data: slotsData,
        pagination: paginationData,
        timestamp: now
      });

      setSlots(slotsData);
      setSlotsPagination(paginationData);
      console.log('✅ Slots loaded:', slotsData.length);
      console.log('✅ Pagination:', paginationData);
    } catch (error) {
      console.error('❌ Failed to load slots:', error);
      showAlert('Failed to load availability slots', 'error');
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [lastRequestTime, slotsPagination.per_page]);

  // ✅ OPTIMIZED: Initial load with debounce
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadAvailabilitySlots(1, filterOptions);
    }, 300); // 300ms debounce

    return () => clearTimeout(timeoutId);
  }, [filterOptions]); // Removed loadAvailabilitySlots from dependencies

  // ✅ OPTIMIZED: Handle create slot with cache clearing
  const handleCreateSlot = async (slotData) => {
    try {
      console.log('🗓️ Creating new slot...', slotData);
      const response = await panelistAPI.createAvailabilitySlots(slotData);
      showAlert(
        `Successfully created ${response.data.total_created} availability slot(s)`,
        'success'
      );
      setCreateDialogOpen(false);
      
      // ✅ Clear cache and force refresh
      requestCache.current.clear();
      await loadAvailabilitySlots(slotsPagination.page, { ...filterOptions, _refresh: Date.now() }, true);
      
      console.log('✅ Slot created and data refreshed');
    } catch (error) {
      console.error('❌ Failed to create slot:', error);
      showAlert('Failed to create availability slot', 'error');
    }
  };

  const handleEditSlot = async (slotId, slotData) => {
    try {
      console.log('✏️ Updating slot...', slotId, slotData);
      const response = await panelistAPI.updateAvailabilitySlot(slotId, slotData);
      
      showAlert('Availability slot updated successfully', 'success');
      setEditDialogOpen(false);
      setSelectedSlot(null);
      
      // ✅ Clear cache and refresh
      requestCache.current.clear();
      loadAvailabilitySlots(filterOptions, true);
      
      console.log('✅ Slot updated successfully');
    } catch (error) {
      console.error('❌ Failed to update slot:', error);
      showAlert('Failed to update availability slot', 'error');
    }
  };

  const freeCompletedSlots = async () => {
    try {
        const response = await panelistAPI.freeCompletedSlots();
        if (response.data.status === 'success') {
            showAlert(`Freed ${response.data.freed_count} orphaned slots`, 'success');
            loadAvailabilitySlots(slotsPagination.page, filterOptions, true); // Refresh the slots list
        }
    } catch (error) {
        showAlert('Failed to free completed slots', 'error');
    }
  };

  const handleDeleteSlot = async (slotId) => {
    try {
      console.log(`🗑️ Deleting slot ${slotId}...`);
      
      // Show confirmation dialog
      const confirmed = window.confirm('Are you sure you want to delete this availability slot?');
      if (!confirmed) {
        return;
      }

      const response = await panelistAPI.deleteAvailabilitySlot(slotId);
      
      console.log('✅ Delete response:', response.data);
      
      if (response.data.status === 'success') {
        console.log(`✅ Slot ${slotId} deleted successfully`);
        
        // Show success message using your existing alert system
        showAlert(response.data.message || 'Slot deleted successfully', 'success');
        
        // ✅ Clear cache and refresh
        requestCache.current.clear();
        loadAvailabilitySlots(slotsPagination.page, filterOptions, true);
      }
    } catch (error) {
      console.error('❌ Error deleting slot:', error);
      console.error('❌ Error response:', error.response?.data);
      
      // Show error message using your existing alert system
      const errorMessage = error.response?.data?.error || 'Failed to delete slot';
      showAlert(`Error: ${errorMessage}`, 'error');
    }
  };

  // ===== DIALOG HANDLERS =====
  const openEditDialog = (slot) => {
    setSelectedSlot(slot);
    setEditDialogOpen(true);
  };

  const closeDialogs = () => {
    setCreateDialogOpen(false);
    setEditDialogOpen(false);
    setSelectedSlot(null);
  };

  // ===== UTILITY FUNCTIONS =====
  const showAlert = (message, severity) => {
    setAlert({ open: true, message, severity });
    setTimeout(() => setAlert({ open: false, message: '', severity: 'info' }), 5000);
  };

  // ✅ OPTIMIZED: Manual refresh with cache clearing
  const handleRefresh = () => {
    requestCache.current.clear();
    loadAvailabilitySlots(slotsPagination.page, filterOptions, true);
  };

  const handleCleanupSlots = async () => {
    try {
      console.log('🧹 Cleaning up completed interview slots...');
      const response = await panelistAPI.cleanupCompletedInterviewSlots();
      
      if (response.data.status === 'success') {
        showAlert(response.data.message, 'success');
        // Refresh slots
        requestCache.current.clear();
        await loadAvailabilitySlots(slotsPagination.page, filterOptions, true);
      }
    } catch (error) {
      console.error('❌ Cleanup failed:', error);
      showAlert('Failed to cleanup slots', 'error');
    }
  };

  // ===== PAGINATION HANDLER =====
  const handleSlotsPageChange = (newPage) => {
    if (newPage >= 1 && newPage <= slotsPagination.pages && !loading) {
      loadAvailabilitySlots(newPage, filterOptions);
    }
  };

  // ===== STATISTICS CALCULATIONS =====
  const statistics = {
    total_slots: slotsPagination.total,
    available_slots: slots.filter(s => s.is_available && !s.is_booked).length,
    booked_slots: slots.filter(s => s.is_booked).length,
    today_slots: slots.filter(s => {
      const slotDate = new Date(s.start_datetime);
      const today = new Date();
      return slotDate.toDateString() === today.toDateString();
    }).length
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading Availability Management...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {alert.open && (
        <Alert 
          severity={alert.severity} 
          sx={{ mb: 2 }} 
          onClose={() => setAlert({ open: false, message: '', severity: 'info' })}
        >
          {alert.message}
        </Alert>
      )}

      {/* ===== STATISTICS CARDS ===== */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6} lg={3}>
          <Card elevation={3}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total Slots
              </Typography>
              <Typography variant="h4" color="primary">
                {statistics.total_slots}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6} lg={3}>
          <Card elevation={3}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Available Slots
              </Typography>
              <Typography variant="h4" color="success.main">
                {statistics.available_slots}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6} lg={3}>
          <Card elevation={3}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Booked Slots
              </Typography>
              <Typography variant="h4" color="warning.main">
                {statistics.booked_slots}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid item xs={12} md={6} lg={3}>
          <Card elevation={3}>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Today's Slots
              </Typography>
              <Typography variant="h4" color="info.main">
                {statistics.today_slots}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* ===== MAIN TABS ===== */}
      <Tabs 
        value={activeTab} 
        onChange={(e, newValue) => setActiveTab(newValue)} 
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}
      >
        <Tab 
          label="All Slots" 
          icon={<CalendarIcon />}
          iconPosition="start"
        />
        <Tab 
          label="Available Only" 
          icon={<AvailableIcon />}
          iconPosition="start"
        />
        <Tab 
          label="Booked Slots" 
          icon={<BookedIcon />}
          iconPosition="start"
        />
      </Tabs>

      <Button
        variant="outlined"
        onClick={handleCleanupSlots}
        startIcon={<RefreshIcon />}
        sx={{ mr: 1, mb: 2 }}
      >
        Cleanup Completed Interviews
      </Button>

      {/* ===== SLOTS LIST ===== */}
      <SlotsList
        slots={slots}
        activeTab={activeTab}
        pagination={slotsPagination}
        onPageChange={handleSlotsPageChange}
        onEditSlot={openEditDialog}
        onDeleteSlot={handleDeleteSlot}
        onRefresh={handleRefresh}
        loading={loading}
      />

      {/* ===== CREATE SLOT FAB ===== */}
      <Fab
        color="primary"
        aria-label="create availability slot"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={() => setCreateDialogOpen(true)}
      >
        <AddIcon />
      </Fab>

      {/* ===== CREATE SLOT DIALOG ===== */}
      <Dialog 
        open={createDialogOpen} 
        onClose={closeDialogs}
        maxWidth="md"
        fullWidth
        PaperProps={{ elevation: 10 }}
      >
        <DialogTitle>
          <Typography variant="h5">Create Availability Slots</Typography>
        </DialogTitle>
        <DialogContent>
          <AvailabilitySlotForm
            onSubmit={handleCreateSlot}
            onCancel={closeDialogs}
            mode="create"
          />
        </DialogContent>
      </Dialog>

      {/* ===== EDIT SLOT DIALOG ===== */}
      <Dialog 
        open={editDialogOpen} 
        onClose={closeDialogs}
        maxWidth="md"
        fullWidth
        PaperProps={{ elevation: 10 }}
      >
        <DialogTitle>
          <Typography variant="h5">Edit Availability Slot</Typography>
        </DialogTitle>
        <DialogContent>
          <AvailabilitySlotForm
            initialData={selectedSlot}
            onSubmit={(data) => handleEditSlot(selectedSlot.id, data)}
            onCancel={closeDialogs}
            mode="edit"
          />
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AvailabilityManagement;
